export class STVCalculator {
  constructor(votes, options, seatsToFill = 1) {
    this.originalVotes = votes; // Original votes data for reference
    this.votes = this.formatVotes(votes); // Formatted votes for calculation
    this.options = options; // The available options
    this.seatsToFill = 1; // Always 1 seat for simplicity (ignore seatsToFill parameter)
    this.winners = []; // Array to store winners
    this.eliminatedOptions = []; // Options that have been eliminated
    this.rounds = []; // Store data for each round
    this.quota = this.calculateQuota(this.votes.length, 1); // Winning threshold - always using 1 seat
  }

  /**
   * Format votes from database format to algorithm format
   * @param {Array} votesData - Raw votes data from the database
   * @returns {Array} Formatted votes for STV calculation
   */
  formatVotes(votesData) {
    // Filter out any votes with no rankings
    const validVotes = votesData.filter(vote => vote.rankings && Object.keys(vote.rankings).length > 0);
    
    return validVotes.map(vote => {
      // Extract rankings data from vote object
      const rankings = vote.rankings;
      
      // Create a formatted vote with preferences in order
      return {
        preferences: Object.keys(rankings)
          .map(rank => ({
            rank: parseInt(rank.replace('rank', '')),
            optionId: rankings[rank]
          }))
          .sort((a, b) => a.rank - b.rank)
          .map(item => item.optionId)
      };
    });
  }

  /**
   * Calculate the Droop quota
   * @param {number} numVotes - Total number of valid votes
   * @param {number} numSeats - Number of seats to fill (always 1 for our app)
   * @returns {number} The quota (minimum votes needed to secure a seat)
   */
  calculateQuota(numVotes, numSeats) {
    // Droop quota: floor(votes / (seats + 1)) + 1
    if (numVotes === 0) return 1; // If no votes, set minimum quota of 1
    return Math.floor(numVotes / (numSeats + 1)) + 1;
  }

  /**
   * Count first preference votes for each option
   * @param {Array} votes - Current votes
   * @returns {Object} First preference counts for each option
   */
  countFirstPreferences(votes) {
    const counts = {};
    
    // Initialize counts for all options
    this.options.forEach(option => {
      counts[option.id] = 0;
    });
    
    // Count first preferences
    votes.forEach(vote => {
      if (vote.preferences.length > 0) {
        const firstPref = vote.preferences[0];
        if (counts[firstPref] !== undefined) {
          counts[firstPref]++;
        }
      }
    });
    
    return counts;
  }

  /**
   * Find options that meet or exceed the quota
   * @param {Object} counts - Vote counts for each option
   * @returns {Array} Options that meet or exceed the quota
   */
  findWinners(counts) {
    const winners = [];
    
    Object.entries(counts).forEach(([optionId, count]) => {
      if (count >= this.quota && !this.winners.includes(optionId) && !this.eliminatedOptions.includes(optionId)) {
        winners.push({
          optionId,
          count
        });
      }
    });
    
    return winners;
  }

  /**
   * Redistribute surplus votes from a winner
   * @param {string} winnerId - ID of the winning option
   * @param {number} surplus - Number of surplus votes to redistribute
   * @param {Array} votes - Current votes
   * @returns {Array} Updated votes after redistribution
   */
  redistributeSurplus(winnerId, surplus, votes) {
    // Find votes that had the winner as first preference
    const winnerVotes = votes.filter(vote => 
      vote.preferences.length > 0 && vote.preferences[0] === winnerId
    );
    
    // Calculate transfer value
    const transferValue = surplus / winnerVotes.length;
    
    // Create a deep copy of votes
    const updatedVotes = JSON.parse(JSON.stringify(votes));
    
    // Process each vote
    updatedVotes.forEach(vote => {
      if (vote.preferences.length > 0 && vote.preferences[0] === winnerId) {
        // Remove the winner from preferences
        vote.preferences.shift();
        
        // Add transfer value property to the vote
        vote.transferValue = (vote.transferValue || 1) * transferValue;
      }
    });
    
    return updatedVotes;
  }

  /**
   * Find the option with the lowest first preference count
   * @param {Object} counts - Vote counts for each option
   * @returns {string} ID of the option with the lowest count
   */
  findLowestOption(counts) {
    let lowestCount = Infinity;
    let lowestOptionId = null;
    
    Object.entries(counts).forEach(([optionId, count]) => {
      if (
        count < lowestCount && 
        !this.winners.includes(optionId) && 
        !this.eliminatedOptions.includes(optionId)
      ) {
        lowestCount = count;
        lowestOptionId = optionId;
      }
    });
    
    return lowestOptionId;
  }

  /**
   * Eliminate an option and redistribute its votes
   * @param {string} optionId - ID of the option to eliminate
   * @param {Array} votes - Current votes
   * @returns {Array} Updated votes after elimination
   */
  eliminateOption(optionId, votes) {
    // Create a deep copy of votes
    const updatedVotes = JSON.parse(JSON.stringify(votes));
    
    // Process each vote
    updatedVotes.forEach(vote => {
      // Remove the eliminated option from preferences
      vote.preferences = vote.preferences.filter(pref => pref !== optionId);
    });
    
    return updatedVotes;
  }

  /**
   * Run the full STV calculation
   * @returns {Object} Results of the STV calculation
   */
  calculate() {
    console.log("Starting STV calculation with", this.votes.length, "votes");
    console.log("Quota to win:", this.quota);
    
    let currentVotes = [...this.votes];
    let roundNum = 0;
    
    // Continue until all seats are filled or no more options
    while (
      this.winners.length < this.seatsToFill && 
      this.winners.length + this.eliminatedOptions.length < this.options.length
    ) {
      roundNum++;
      console.log("Processing round", roundNum);
      
      // Count first preferences
      const counts = this.countFirstPreferences(currentVotes);
      console.log("Vote counts for round", roundNum, ":", counts);
      
      // Find winners for this round
      const roundWinners = this.findWinners(counts);
      console.log("Winners for round", roundNum, ":", roundWinners);
      
      // Store round data
      const roundData = {
        round: roundNum,
        counts: {...counts},
        winners: roundWinners.map(w => w.optionId),
        eliminated: null,
        quota: this.quota
      };
      
      // Process winners if any
      if (roundWinners.length > 0) {
        // Sort winners by count (highest first) to process them in order
        roundWinners.sort((a, b) => b.count - a.count);
        
        // Process each winner
        for (const winner of roundWinners) {
          this.winners.push(winner.optionId);
          console.log("Adding winner:", winner.optionId, "with count:", winner.count);
          
          // If we have surplus votes, redistribute them
          const surplus = winner.count - this.quota;
          if (surplus > 0) {
            console.log("Redistributing surplus of", surplus, "votes");
            currentVotes = this.redistributeSurplus(winner.optionId, surplus, currentVotes);
          }
        }
      } else {
        // No winners this round, eliminate the lowest option
        const lowestOptionId = this.findLowestOption(counts);
        
        if (lowestOptionId) {
          console.log("Eliminating option:", lowestOptionId, "with count:", counts[lowestOptionId]);
          this.eliminatedOptions.push(lowestOptionId);
          roundData.eliminated = lowestOptionId;
          
          // Redistribute votes from the eliminated option
          currentVotes = this.eliminateOption(lowestOptionId, currentVotes);
        }
      }
      
      this.rounds.push(roundData);
      
      // If all remaining candidates have the same number of votes and no one meets the quota
      // This is to prevent infinite loops
      const remainingCounts = Object.entries(counts)
        .filter(([id]) => !this.winners.includes(id) && !this.eliminatedOptions.includes(id))
        .map(([, count]) => count);
      
      if (remainingCounts.length > 0 && 
          remainingCounts.every(count => count === remainingCounts[0]) && 
          remainingCounts[0] < this.quota) {
        console.log("Tie detected among remaining options");
        
        // Add remaining options in order of original vote count
        const remainingOptions = Object.entries(counts)
          .filter(([id]) => !this.winners.includes(id) && !this.eliminatedOptions.includes(id))
          .sort((a, b) => b[1] - a[1])
          .map(([id]) => id);
        
        console.log("Selecting winner from tied options:", remainingOptions);
        
        // Add as many as needed to fill seats
        for (const optionId of remainingOptions) {
          if (this.winners.length < this.seatsToFill) {
            this.winners.push(optionId);
            console.log("Adding winner from tied options:", optionId);
          } else {
            break;
          }
        }
        
        // Add a final round to the data
        this.rounds.push({
          round: roundNum + 1,
          counts: {...counts},
          winners: remainingOptions.slice(0, this.seatsToFill - this.winners.length + remainingOptions.length),
          eliminated: null,
          quota: this.quota,
          note: "Tie broken based on original vote count"
        });
        
        break;
      }
    }
    
    // Map winner IDs to full option objects
    const winnerObjects = this.winners.map(winnerId => 
      this.options.find(option => option.id === winnerId)
    );
    
    console.log("Final winners:", winnerObjects);
    
    return {
      winners: winnerObjects,
      rounds: this.rounds,
      quota: this.quota,
      totalVotes: this.votes.length
    };
  }
}

/**
 * Generate a detailed report from STV calculation results
 * @param {Object} results - Results from STVCalculator
 * @param {Array} options - All available options
 * @returns {Object} Formatted report
 */
export function generateSTVReport(results, options) {
  const { winners, rounds, quota, totalVotes } = results;
  
  // Map option IDs to names for easier display
  const optionMap = {};
  options.forEach(option => {
    optionMap[option.id] = option.name || option.text;
  });
  
  // Format rounds data for display
  const formattedRounds = rounds.map(round => {
    const formattedCounts = {};
    
    // Format counts with option names
    Object.entries(round.counts).forEach(([optionId, count]) => {
      formattedCounts[optionMap[optionId] || optionId] = count;
    });
    
    // Format winners with option names
    const formattedWinners = (round.winners || []).map(winnerId => 
      optionMap[winnerId] || winnerId
    );
    
    // Format eliminated option name
    const formattedEliminated = round.eliminated ? 
      (optionMap[round.eliminated] || round.eliminated) : null;
    
    return {
      round: round.round,
      counts: formattedCounts,
      winners: formattedWinners,
      eliminated: formattedEliminated,
      quota
    };
  });
  
  // Format winners with option names
  const formattedWinners = winners.map(winner => ({
    name: winner.name || winner.text,
    id: winner.id
  }));
  
  // Create the final report
  return {
    title: "Ranked Choice Voting Results",
    timestamp: new Date().toISOString(),
    totalVotes,
    quota,
    winners: formattedWinners,
    rounds: formattedRounds
  };
}

/**
 * Export results to CSV format
 * @param {Object} report - Formatted report from generateSTVReport
 * @returns {string} CSV content
 */
export function exportToCSV(report) {
  const { title, timestamp, totalVotes, quota, winners, rounds } = report;
  
  let csv = `${title}\n`;
  csv += `Generated: ${new Date(timestamp).toLocaleString()}\n`;
  csv += `Total Votes: ${totalVotes}\n`;
  csv += `Quota: ${quota}\n\n`;
  
  csv += 'Winners:\n';
  winners.forEach((winner, index) => {
    csv += `${index + 1}. ${winner.name}\n`;
  });
  
  csv += '\nRounds:\n';
  
  // Get all unique option names across all rounds
  const allOptions = new Set();
  rounds.forEach(round => {
    Object.keys(round.counts).forEach(option => {
      allOptions.add(option);
    });
  });
  
  // Convert to array and sort alphabetically
  const optionNames = Array.from(allOptions).sort();
  
  // Create header row
  csv += `Round,${optionNames.join(',')},Winners,Eliminated\n`;
  
  // Add data for each round
  rounds.forEach(round => {
    let row = `${round.round}`;
    
    // Add count for each option
    optionNames.forEach(option => {
      row += `,${round.counts[option] || 0}`;
    });
    
    // Add winners and eliminated
    row += `,${round.winners.join(' & ') || ''}`;
    row += `,${round.eliminated || ''}`;
    
    csv += row + '\n';
  });
  
  return csv;
}